import React, { useState } from 'react';
import { X, Plus, Minus, Trash2, ShoppingBag } from 'lucide-react';
export type CartRecipe = {
  id: string;
  name: string;
  thumbnail: string;
  defaultServings: number;
  servings: number;
  ingredients: Array<{
    item: string;
    amount: number;
    unit: string;
    included: boolean;
  }>;
};
export type CustomIngredient = {
  id: string;
  item: string;
  amount: number;
  unit: string;
};
interface ShoppingCartProps {
  isOpen: boolean;
  onClose: () => void;
  recipes: CartRecipe[];
  customIngredients: CustomIngredient[];
  onUpdateServings: (recipeId: string, servings: number) => void;
  onToggleIngredient: (recipeId: string, ingredientIndex: number) => void;
  onRemoveRecipe: (recipeId: string) => void;
  onAddCustomIngredient: (ingredient: CustomIngredient) => void;
  onRemoveCustomIngredient: (id: string) => void;
  onCheckout: (store: 'kroger' | 'meijer' | 'walmart') => void;
  shouldPulseIngredient?: boolean;
  shouldPulseKroger?: boolean;
}
export function ShoppingCart({
  isOpen,
  onClose,
  recipes,
  customIngredients,
  onUpdateServings,
  onToggleIngredient,
  onRemoveRecipe,
  onAddCustomIngredient,
  onRemoveCustomIngredient,
  onCheckout,
  shouldPulseIngredient = false,
  shouldPulseKroger = false
}: ShoppingCartProps) {
  const [newIngredientItem, setNewIngredientItem] = useState('');
  const [newIngredientAmount, setNewIngredientAmount] = useState('');
  const [newIngredientUnit, setNewIngredientUnit] = useState('');
  const handleAddCustom = () => {
    if (newIngredientItem.trim()) {
      onAddCustomIngredient({
        id: Date.now().toString(),
        item: newIngredientItem,
        amount: parseFloat(newIngredientAmount) || 1,
        unit: newIngredientUnit
      });
      setNewIngredientItem('');
      setNewIngredientAmount('');
      setNewIngredientUnit('');
    }
  };
  const totalItems = recipes.reduce((sum, recipe) => sum + recipe.ingredients.filter(i => i.included).length, 0) + customIngredients.length;
  if (!isOpen) return null;
  return <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black bg-opacity-30 z-40" onClick={onClose} data-id="element-646" />

      {/* Cart Modal - Centered */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none" data-id="element-647">
        <div className="w-full max-w-2xl max-h-[90vh] bg-white border-2 border-gray-800 flex flex-col pointer-events-auto" data-id="element-648">
          {/* Header */}
          <div className="border-b-2 border-gray-200 p-4 flex items-center justify-between bg-gray-50" data-id="element-649">
            <div className="flex items-center space-x-3" data-id="element-650">
              <ShoppingBag className="w-5 h-5 text-gray-600" data-id="element-651" />
              <div data-id="element-652">
                <h2 className="font-bold text-lg text-gray-900" data-id="element-653">
                  Shopping Cart
                </h2>
                <p className="text-xs font-mono text-gray-500" data-id="element-654">
                  {totalItems} items
                </p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-gray-200 transition-colors" data-id="element-655">
              <X className="w-5 h-5 text-gray-600" data-id="element-656" />
            </button>
          </div>

          {/* Cart Content */}
          <div className="flex-1 overflow-y-auto p-4 space-y-6" data-id="element-657">
            {recipes.length === 0 && customIngredients.length === 0 ? <div className="border-2 border-dashed border-gray-200 p-8 text-center" data-id="element-658">
                <ShoppingBag className="w-12 h-12 text-gray-300 mx-auto mb-3" data-id="element-659" />
                <p className="font-mono text-sm text-gray-400" data-id="element-660">
                  Your cart is empty
                </p>
              </div> : <>
                {/* Recipes */}
                {recipes.map(recipe => {
              const servingMultiplier = recipe.servings / recipe.defaultServings;
              return <div key={recipe.id} className="border-2 border-gray-300 bg-white" data-id="element-661">
                      {/* Recipe Header */}
                      <div className="p-3 border-b border-gray-200 bg-gray-50" data-id="element-662">
                        <div className="flex items-start justify-between gap-3 mb-3" data-id="element-663">
                          <div className="flex items-center space-x-3 flex-1 min-w-0" data-id="element-664">
                            <div className="w-10 h-10 border border-gray-300 bg-gray-100 flex items-center justify-center text-2xl flex-shrink-0" data-id="element-665">
                              {recipe.thumbnail}
                            </div>
                            <span className="font-bold text-sm text-gray-900 truncate" data-id="element-666">
                              {recipe.name}
                            </span>
                          </div>
                          <button onClick={() => onRemoveRecipe(recipe.id)} className="p-1 hover:bg-gray-200 transition-colors flex-shrink-0" data-id="element-667">
                            <Trash2 className="w-4 h-4 text-gray-400" data-id="element-668" />
                          </button>
                        </div>

                        {/* Servings Control */}
                        <div className="flex items-center space-x-2" data-id="element-669">
                          <span className="font-mono text-xs text-gray-500" data-id="element-670">
                            Servings:
                          </span>
                          <div className="flex items-center border border-gray-300" data-id="element-671">
                            <button onClick={() => onUpdateServings(recipe.id, Math.max(1, recipe.servings - 1))} className="px-2 py-1 border-r border-gray-300 hover:bg-gray-100 text-xs" data-id="element-672">
                              <Minus className="w-3 h-3" data-id="element-673" />
                            </button>
                            <span className="px-3 py-1 font-mono text-xs font-bold" data-id="element-674">
                              {recipe.servings}
                            </span>
                            <button onClick={() => onUpdateServings(recipe.id, recipe.servings + 1)} className="px-2 py-1 border-l border-gray-300 hover:bg-gray-100 text-xs" data-id="element-675">
                              <Plus className="w-3 h-3" data-id="element-676" />
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Ingredients with adjusted quantities */}
                      <div className="p-3 space-y-2" data-id="element-677">
                        {recipe.ingredients.map((ingredient, idx) => <button key={idx} onClick={() => onToggleIngredient(recipe.id, idx)} className={`w-full text-left flex items-start space-x-2 text-xs transition-colors ${ingredient.included ? 'text-gray-700' : 'text-gray-400 line-through'} ${shouldPulseIngredient && idx === 0 ? 'animate-pulse-yellow' : ''}`} data-id="element-678">
                            <div className={`w-3 h-3 border flex items-center justify-center flex-shrink-0 mt-0.5 ${ingredient.included ? 'border-gray-900 bg-gray-900' : 'border-gray-300'}`} data-id="element-679">
                              {ingredient.included && <div className="w-1.5 h-1.5 bg-white" data-id="element-680"></div>}
                            </div>
                            <span data-id="element-681">
                              <strong className="font-mono" data-id="element-682">
                                {(ingredient.amount * servingMultiplier).toFixed(1)}{' '}
                                {ingredient.unit}
                              </strong>{' '}
                              {ingredient.item}
                            </span>
                          </button>)}
                      </div>
                    </div>;
            })}

                {/* Custom Ingredients */}
                {customIngredients.length > 0 && <div className="border-2 border-gray-300 bg-white" data-id="element-683">
                    <div className="p-3 border-b border-gray-200 bg-gray-50" data-id="element-684">
                      <span className="font-bold text-sm text-gray-900" data-id="element-685">
                        Custom Items
                      </span>
                    </div>
                    <div className="p-3 space-y-2" data-id="element-686">
                      {customIngredients.map(ingredient => <div key={ingredient.id} className="flex items-start justify-between space-x-2 text-xs text-gray-700" data-id="element-687">
                          <span data-id="element-688">
                            <strong className="font-mono" data-id="element-689">
                              {ingredient.amount} {ingredient.unit}
                            </strong>{' '}
                            {ingredient.item}
                          </span>
                          <button onClick={() => onRemoveCustomIngredient(ingredient.id)} className="p-1 hover:bg-gray-100 transition-colors flex-shrink-0" data-id="element-690">
                            <X className="w-3 h-3 text-gray-400" data-id="element-691" />
                          </button>
                        </div>)}
                    </div>
                  </div>}
              </>}

            {/* Add Custom Ingredient */}
            <div className="border-2 border-dashed border-gray-300 p-3 space-y-3" data-id="element-692">
              <label className="font-mono text-xs uppercase tracking-wider text-gray-500 block" data-id="element-693">
                Add Custom Item
              </label>
              <div className="space-y-2" data-id="element-694">
                <input type="text" value={newIngredientItem} onChange={e => setNewIngredientItem(e.target.value)} placeholder="Item name..." className="w-full border border-gray-300 bg-white px-3 py-2 font-mono text-xs focus:border-gray-500 focus:outline-none" data-id="element-695" />
                <div className="flex gap-2" data-id="element-696">
                  <input type="number" value={newIngredientAmount} onChange={e => setNewIngredientAmount(e.target.value)} placeholder="Amount" className="w-20 border border-gray-300 bg-white px-3 py-2 font-mono text-xs focus:border-gray-500 focus:outline-none" data-id="element-697" />
                  <input type="text" value={newIngredientUnit} onChange={e => setNewIngredientUnit(e.target.value)} placeholder="Unit" className="flex-1 border border-gray-300 bg-white px-3 py-2 font-mono text-xs focus:border-gray-500 focus:outline-none" data-id="element-698" />
                </div>
                <button onClick={handleAddCustom} disabled={!newIngredientItem.trim()} className="w-full flex items-center justify-center space-x-2 px-3 py-2 bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-id="element-699">
                  <Plus className="w-3 h-3" data-id="element-700" />
                  <span className="font-mono text-xs uppercase tracking-widest" data-id="element-701">
                    Add Item
                  </span>
                </button>
              </div>
            </div>
          </div>

          {/* Checkout Footer WITH PULSING KROGER BUTTON */}
          {totalItems > 0 && <div className="border-t-2 border-gray-200 p-4 bg-gray-50 space-y-3" data-id="element-702">
              <p className="font-mono text-xs uppercase tracking-wider text-gray-500 text-center" data-id="element-703">
                Select Store
              </p>
              <div className="grid grid-cols-3 gap-2" data-id="element-704">
                <button onClick={() => onCheckout('kroger')} className={`px-3 py-3 border-2 border-gray-300 hover:border-gray-900 hover:bg-gray-900 hover:text-white transition-colors font-mono text-xs uppercase tracking-widest ${shouldPulseKroger ? 'animate-pulse-yellow' : ''}`} data-id="element-705">
                  Kroger
                </button>
                <button onClick={() => onCheckout('meijer')} className="px-3 py-3 border-2 border-gray-300 hover:border-gray-900 hover:bg-gray-900 hover:text-white transition-colors font-mono text-xs uppercase tracking-widest" data-id="element-706">
                  Meijer
                </button>
                <button onClick={() => onCheckout('walmart')} className="px-3 py-3 border-2 border-gray-300 hover:border-gray-900 hover:bg-gray-900 hover:text-white transition-colors font-mono text-xs uppercase tracking-widest" data-id="element-707">
                  Walmart
                </button>
              </div>
            </div>}
        </div>
      </div>
    </>;
}